// Glider CDN token icon mappings
// Maps assetId (contractAddress:chainId) to token symbol for CDN URLs

import type { EthOrSolanaAssetIdFormat } from "@repo/types";
import { normalizeAssetId as normalizeAssetIdUtil } from "./asset";

// Default CDN URL base if not provided
const DEFAULT_GLIDER_CDN_URL = "https://cdn.glider.fi";

// Token icons path within the CDN
export const GLIDER_CDN_TOKEN_ICONS_PATH = "/tokenicons/tokens/background/";

// Normalize assetId using multi-VM safe normalization (EVM lowercase, Solana preserve case)
const normalizeAssetId = (assetId: string): string => {
  try {
    return normalizeAssetIdUtil(assetId as EthOrSolanaAssetIdFormat);
  } catch {
    // Fallback for invalid formats - just return lowercase for backwards compat
    return assetId.toLowerCase();
  }
};

// Raw mapping of assetId to token symbol (case doesn't matter - will be normalized)
const RAW_ASSET_ID_TO_SYMBOL_MAP: Record<string, string> = {
  "0x2ae3f1ec7f1f5012cfeab0185bfc7aa3cf0dec22:8453": "CBETH", // USD Coin
  // === Base Chain (8453) ===

  // Stablecoins
  "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913:8453": "USDC", // Tether USD
  "0x4200000000000000000000000000000000000006:8453": "ETH", // Curve.Fi USD Stablecoin - TODO: Add address

  // ETH variants
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee:8453": "ETH", // Native ETH
  "0xfde4c96c8593536e31f229ea8f37b2ada2699bb2:8453": "USDT", // WETH (wrapped ETH) - uses ETH icon
  "0xplaceholder_aave:8453": "AAVE", // Coinbase Staked ETH
  // "0x940181a94a35a4569e4529a3cdfb74e38fd98631:8453": "AERO", // Aerodrome (we don't have AERO.svg but it's in the list)
  "0xplaceholder_bal:8453": "BAL", // Rocket Pool ETH - TODO: Add address
  "0xplaceholder_bmx:8453": "BMX", // Uniswap - TODO: Add address
  "0xplaceholder_crvusd:8453": "CRVUSD", // Balancer - TODO: Add address
  "0xplaceholder_ctsi:8453": "CTSI", // Aave - TODO: Add address
  "0xplaceholder_defi:8453": "DEFI", // yearn.finance - TODO: Add address
  "0xplaceholder_edge:8453": "EDGE", // SPX6900 - TODO: Add address
  "0xplaceholder_ftn:8453": "FTN", // Virtual Protocol - TODO: Add address

  // AI/Gaming tokens
  "0xplaceholder_game:8453": "GAME", // LayerZero - TODO: Add address
  "0xplaceholder_hai:8453": "HAI", // Bridged FTN (OrtakSea) - TODO: Add address
  "0xplaceholder_krl:8453": "KRL", // BasedPepe - TODO: Add address
  "0xplaceholder_mochi:8453": "MOCHI", // Toshi - TODO: Add address

  // Meme tokens
  // "0x4ed4e862860bed51a9570b96d89af5e1b0efefed:8453": "DEGEN", // Degen
  "0xplaceholder_pepe:8453": "PEPE", // Mochi - TODO: Add address
  "0xplaceholder_reth:8453": "RETH", // GAME by Virtuals - TODO: Add address
  "0xplaceholder_sdex:8453": "SDEX", // Cartesi Token - TODO: Add address
  "0xplaceholder_sofi:8453": "SOFI", // Venus XVS - TODO: Add address

  // Popular tokens
  "0xplaceholder_spx:8453": "SPX", // Yield Guild Games Token - TODO: Add address
  "0xplaceholder_tig:8453": "TIG", // Edge - TODO: Add address
  "0xplaceholder_toshi:8453": "TOSHI", // HackenAI - TODO: Add address

  // DeFi tokens
  "0xplaceholder_uni:8453": "UNI", // Ambire Wallet - TODO: Add address
  "0xplaceholder_virtual:8453": "VIRTUAL", // BMX - TODO: Add address
  "0xplaceholder_wallet:8453": "WALLET", // DEFI.ssi - TODO: Add address

  // Other tokens with available icons
  "0xplaceholder_xvs:8453": "XVS", // Kryll - TODO: Add address
  "0xplaceholder_yfi:8453": "YFI", // SmarDex Token - TODO: Add address
  "0xplaceholder_ygg:8453": "YGG", // Rai.Finance - TODO: Add address
  "0xplaceholder_zro:8453": "ZRO", // The Innovation Game - TODO: Add address

  // === BNB Chain (56) ===
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee:56": "BNB", // Native BNB
  "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c:56": "BNB", // Wrapped BNB
  "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d:56": "USDC", // USD Coin
  "0x55d398326f99059ff775485246999027b3197955:56": "USDT", // Tether USD
};

// Create the normalized mapping with all keys lowercase
const ASSET_ID_TO_SYMBOL_MAP: Record<string, string> = Object.entries(
  RAW_ASSET_ID_TO_SYMBOL_MAP,
).reduce(
  (acc, [key, value]) => {
    acc[normalizeAssetId(key)] = value;
    return acc;
  },
  {} as Record<string, string>,
);

export interface TokenIconInfo {
  symbol: string;
  cdnUrl: string;
}

/**
 * Get token icon info from Glider CDN for a given assetId
 * @param assetId - Format: contractAddress:chainId
 * @param cdnUrlBase - Optional CDN URL base (defaults to DEFAULT_GLIDER_CDN_URL)
 * @returns TokenIconInfo if found, null otherwise
 */
export const getGliderTokenIcon = (
  assetId: string,
  cdnUrlBase?: string,
): TokenIconInfo | null => {
  const normalizedAssetId = normalizeAssetId(assetId);
  const symbol = ASSET_ID_TO_SYMBOL_MAP[normalizedAssetId];

  if (!symbol) {
    return null;
  }

  const baseUrl = cdnUrlBase || DEFAULT_GLIDER_CDN_URL;

  return {
    cdnUrl: `${baseUrl}${GLIDER_CDN_TOKEN_ICONS_PATH}${symbol}.svg`,
    symbol,
  };
};

/**
 * Check if we have a custom icon for this assetId
 */
export const hasGliderTokenIcon = (assetId: string): boolean => {
  const normalizedAssetId = normalizeAssetId(assetId);
  return normalizedAssetId in ASSET_ID_TO_SYMBOL_MAP;
};
